'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { Button } from '@benzinga/core-ui';
import { MediaCard } from '../MediaCard';
import { DateTime } from 'luxon';
import { PodcastCardData } from '../PodcastCard';

export interface ShowsAndPodcastsProps {
  defaultTab?: 'shows' | 'podcasts';
  podcasts: PodcastCardData[];
  showMoreButton?: boolean;
  shows: PodcastCardData[];
}

export const ShowsAndPodcasts: React.FC<ShowsAndPodcastsProps> = ({
  defaultTab = 'shows',
  podcasts,
  showMoreButton = true,
  shows,
}) => {
  const [activeTab, setActiveTab] = React.useState<'shows' | 'podcasts'>(defaultTab);

  if (!Array.isArray(podcasts)) return null;

  const handleChangeTab = (newTab: 'shows' | 'podcasts') => {
    setActiveTab(newTab);
  };

  return (
    <PodcastsListWrapper className="grid bg-white gap-2 p-2 my-4 rounded-md border">
      <div className="shows-and-podcasts-tabs flex rounded overflow-hidden">
        <button className={activeTab === 'shows' ? 'active' : undefined} onClick={() => handleChangeTab('shows')}>
          Shows
        </button>
        <button className={activeTab === 'podcasts' ? 'active' : undefined} onClick={() => handleChangeTab('podcasts')}>
          Podcasts
        </button>
      </div>
      <div className="podcasts-list grid gap-2">
        {activeTab === 'shows'
          ? shows.slice(0, 3).map((show, index) => {
              return (
                <MediaCard
                  contentId="Podcasts"
                  image={show.image}
                  key={`shows-item-${index}-${show.id}`}
                  link={show.link}
                  subtitle={show.description}
                  title={show.show_name}
                />
              );
            })
          : podcasts.slice(0, 3).map((podcast, index) => {
              const date = podcast.date ? DateTime.fromJSDate(new Date(podcast.date)).toFormat('MMM dd, yyyy') : null;
              return (
                <MediaCard
                  contentId="Podcasts"
                  image={podcast.image}
                  key={`podcasts-item-${index}-${podcast.id}`}
                  link={podcast.link}
                  subtitle={date}
                  title={podcast.title}
                />
              );
            })}
      </div>
      {showMoreButton && (
        <Button
          as="a"
          className="show-more-button text-blue-500 font-medium text-center text-sm"
          data-action="Browse All Podcasts Footer Button Click"
          href="/podcasts"
          target="_blank"
          variant="flat-blue"
        >
          SHOW MORE
        </Button>
      )}
    </PodcastsListWrapper>
  );
};

export const PodcastsListWrapper = styled.div`
  .shows-and-podcasts-tabs {
    button {
      width: 100%;
      font-size: 0.875rem;
      font-weight: 600;
      line-height: 1;
      text-transform: uppercase;
      padding: 0.5rem 0.25rem;
      color: ${({ theme }) => theme.colorPalette.blue500};

      &.active {
        background: ${({ theme }) => theme.colorPalette.blue500};
        color: #ffffff;
      }

      &:not(.active):hover {
        background: ${({ theme }) => theme.colorPalette.blue100};
      }
    }
  }

  .media-card-title,
  .media-card-subtitle {
    text-overflow: ellipsis;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }

  .media-card-title {
    -webkit-line-clamp: 2;
  }

  .media-card-subtitle {
    -webkit-line-clamp: 3;
  }

  .show-more-button {
    padding: 0.35rem 0.25rem;
  }
`;
